export type GateState = string;
export type GateActionMode = 'reveal' | 'download' | 'custom';

export interface GateActionConfig {
  id: string;
  mode?: GateActionMode;
}

export interface GateConfig {
  id: string;
  statePath: string;
  storageKey?: string;
  defaultState: GateState;
  formState?: GateState;
  successState: GateState;
  failureState?: GateState;
  expiredState?: GateState;
  persistStates?: GateState[];
  persist?: boolean;
  formSectionId?: string;
  siteToken?: string | null;
  submissionEndpoint?: string | null;
  supabaseAnonKey?: string | null;
  actions?: GateActionConfig[];
}

export interface PendingGateAction {
  gateId: string;
  actionId?: string;
  mode?: GateActionMode;
  perform?: () => void;
}

export interface GateManagerOptions {
  gates: GateConfig[];
  storage?: Storage;
  onStateChange?: (gateId: string, state: GateState, statePath: string) => void;
  performAction?: (action: PendingGateAction) => void;
  submitLead?: (gate: GateConfig, payload: Record<string, unknown>) => Promise<void>;
}

export interface GateSnapshot {
  gateId: string;
  statePath: string;
  state: GateState;
}

export class GateManager {
  private gates = new Map<string, GateConfig>();
  private states = new Map<string, GateState>();
  private pendingAction: PendingGateAction | null = null;
  private storage?: Storage;
  private onStateChange?: GateManagerOptions['onStateChange'];
  private performActionCallback?: GateManagerOptions['performAction'];
  private submitLeadCallback?: GateManagerOptions['submitLead'];

  constructor(options: GateManagerOptions) {
    this.storage = options.storage;
    this.onStateChange = options.onStateChange;
    this.performActionCallback = options.performAction;
    this.submitLeadCallback = options.submitLead;

    for (const gate of options.gates) {
      const normalized = this.normalizeGate(gate);
      this.gates.set(normalized.id, normalized);
      this.states.set(normalized.id, this.loadInitialState(normalized));
    }
  }

  getGate(gateId?: string): GateConfig | undefined {
    return this.gates.get(gateId || this.getDefaultGateId());
  }

  getState(gateId?: string): GateState {
    const id = gateId || this.getDefaultGateId();
    const gate = this.gates.get(id);
    return this.states.get(id) || this.getDefaultState(gate);
  }

  getSnapshots(): GateSnapshot[] {
    return Array.from(this.gates.values()).map((gate) => ({
      gateId: gate.id,
      statePath: this.getStatePath(gate),
      state: this.getState(gate.id),
    }));
  }

  open(actionId?: string, gateId?: string, action?: Partial<PendingGateAction>): void {
    const id = gateId || this.getDefaultGateId();
    if (!this.gates.has(id)) return;

    if (actionId || action) {
      this.pendingAction = {
        gateId: id,
        actionId,
        ...action,
      };
    }

    if (this.isSuccessState(id)) {
      this.replayPendingAction();
      return;
    }

    const gate = this.gates.get(id);
    this.setState(id, this.getFormState(gate), false);
  }

  close(gateId?: string): void {
    const id = gateId || this.pendingAction?.gateId || this.getDefaultGateId();
    const gate = this.gates.get(id);
    if (!gate || this.isSuccessState(id)) return;
    this.pendingAction = null;
    this.setState(id, this.getDefaultState(gate), false);
  }

  async submitLead(gateId: string | undefined, payload: Record<string, unknown>): Promise<void> {
    const gate = this.getGate(gateId);
    if (!gate) return;

    if (this.submitLeadCallback) {
      await this.submitLeadCallback(gate, payload);
      return;
    }

    const endpoint = String(payload.__submissionEndpoint || gate.submissionEndpoint || '');
    const token = String(payload.__siteToken || gate.siteToken || '');
    if (!endpoint || !token) return;

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const anonKey = String(payload.__supabaseAnonKey || gate.supabaseAnonKey || '');
    if (anonKey) headers.apikey = anonKey;
    delete payload.__submissionEndpoint;
    delete payload.__siteToken;
    delete payload.__supabaseAnonKey;

    await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...payload,
        siteToken: token,
      }),
    });
  }

  unlock(gateId?: string): void {
    const id = gateId || this.pendingAction?.gateId || this.getDefaultGateId();
    const gate = this.gates.get(id);
    if (!gate) return;
    this.setState(id, this.getSuccessState(gate), true);
    this.replayPendingAction();
  }

  isUnlocked(gateId?: string): boolean {
    return this.isSuccessState(gateId);
  }

  fail(gateId?: string): void {
    const id = gateId || this.pendingAction?.gateId || this.getDefaultGateId();
    const gate = this.gates.get(id);
    if (!gate?.failureState) return;
    this.setState(id, gate.failureState, false);
  }

  expire(gateId?: string): void {
    const id = gateId || this.getDefaultGateId();
    const gate = this.gates.get(id);
    if (!gate?.expiredState) return;
    this.setState(id, gate.expiredState, false);
  }

  performAction(actionId: string, gateId?: string): void {
    const id = gateId || this.getDefaultGateId();
    this.performActionCallback?.({ gateId: id, actionId });
  }

  private replayPendingAction(): void {
    const action = this.pendingAction;
    this.pendingAction = null;
    if (!action) return;

    if (action.perform) {
      action.perform();
      return;
    }

    if (action.actionId) {
      this.performActionCallback?.(action);
    }
  }

  private setState(gateId: string, state: GateState, persist: boolean): void {
    const gate = this.gates.get(gateId);
    if (!gate) return;

    const previous = this.states.get(gateId);
    this.states.set(gateId, state);

    if (persist && gate.persist !== false && this.shouldPersistState(gate, state)) {
      this.storage?.setItem(this.getStorageKey(gate), JSON.stringify(state));
    }

    if (previous !== state) {
      this.onStateChange?.(gateId, state, this.getStatePath(gate));
    }
  }

  private loadInitialState(gate: GateConfig): GateState {
    const fallback = this.getDefaultState(gate);
    if (gate.persist === false) return fallback;

    try {
      const stored = this.storage?.getItem(this.getStorageKey(gate));
      if (!stored) return fallback;
      const parsed = JSON.parse(stored);
      return typeof parsed === 'string' && this.shouldPersistState(gate, parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  private normalizeGate(gate: GateConfig): GateConfig {
    return {
      ...gate,
      persistStates: gate.persistStates?.length ? gate.persistStates : [gate.successState],
      persist: gate.persist !== false,
      actions: gate.actions || [],
    };
  }

  private getDefaultGateId(): string {
    return this.gates.keys().next().value || 'lead-capture';
  }

  private getStatePath(gate: GateConfig): string {
    return gate.statePath;
  }

  private getDefaultState(gate: GateConfig | undefined): GateState {
    return gate?.defaultState || '';
  }

  private getFormState(gate: GateConfig | undefined): GateState {
    return gate?.formState || gate?.defaultState || '';
  }

  private getSuccessState(gate: GateConfig): GateState {
    return gate.successState;
  }

  private isSuccessState(gateId?: string): boolean {
    const gate = this.getGate(gateId);
    if (!gate) return false;
    return this.getState(gate.id) === this.getSuccessState(gate);
  }

  private shouldPersistState(gate: GateConfig, state: GateState): boolean {
    const persistStates = gate.persistStates?.length ? gate.persistStates : [this.getSuccessState(gate)];
    return persistStates.includes(state);
  }

  private getStorageKey(gate: GateConfig): string {
    if (gate.storageKey) return gate.storageKey;
    return `dlr_state_${this.getStatePath(gate).replace(/\./g, '_')}`;
  }
}

export function createGateManager(options: GateManagerOptions): GateManager {
  return new GateManager(options);
}
